import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

interface GlobeProps {
    size?: number;
}

const Globe3D: React.FC<GlobeProps> = ({ size = 550 }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const width = size;
        const height = size;

        // Scene setup
        const scene = new THREE.Scene();
        // Adjust camera FOV based on size to keep the globe well-framed
        const camera = new THREE.PerspectiveCamera(size < 400 ? 55 : 45, 1, 0.1, 1000);
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

        renderer.setSize(width, height);
        renderer.setPixelRatio(window.devicePixelRatio);
        container.appendChild(renderer.domElement);

        // Orbit Controls (Mouse interaction)
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.enableZoom = false;
        controls.autoRotate = true;
        controls.autoRotateSpeed = 2.0;

        // Create Dot Globe (Less dense for more clarity)
        const geometry = new THREE.SphereGeometry(1.5, 35, 35);
        const material = new THREE.PointsMaterial({
            color: 0xffffff,
            size: size < 400 ? 0.03 : 0.038, /* Slightly smaller points for smaller globe */
            transparent: true,
            opacity: 0.85,
            blending: THREE.AdditiveBlending
        });

        const points = new THREE.Points(geometry, material);
        scene.add(points);

        // Routes group
        const routesGroup = new THREE.Group();
        scene.add(routesGroup);

        // Add robust wireframe lines (Slightly cleaner)
        const wireGeometry = new THREE.SphereGeometry(1.5, 20, 20);
        const wireMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            wireframe: true,
            transparent: true,
            opacity: 0.15
        });
        const wireSphere = new THREE.Mesh(wireGeometry, wireMaterial);
        scene.add(wireSphere);

        // Helper to get a random uniform vector on the sphere surface
        const getRandomUniformVector = (radius: number) => {
            const u = Math.random();
            const v = Math.random();
            const theta = 2 * Math.PI * u;
            const phi = Math.acos(2 * v - 1);
            return new THREE.Vector3(
                radius * Math.sin(phi) * Math.cos(theta),
                radius * Math.sin(phi) * Math.sin(theta),
                radius * Math.cos(phi)
            );
        };

        // Create arcs (Routes that protrude more)
        const createRoute = () => {
            const v1 = getRandomUniformVector(1.5);
            const v2 = getRandomUniformVector(1.5);

            // Ensure points aren't too close for better arc visualization
            if (v1.distanceTo(v2) < 1.0) {
                v2.add(getRandomUniformVector(0.5)).normalize().multiplyScalar(1.5);
            }

            // Variable arc height - Slightly more protruded
            const height = 1.55 + Math.random() * 1;
            const mid = v1.clone().lerp(v2, 0.5).normalize().multiplyScalar(height);
            const curve = new THREE.QuadraticBezierCurve3(v1, mid, v2);

            const lineGeometry = new THREE.BufferGeometry().setFromPoints(curve.getPoints(50));
            const lineMaterial = new THREE.LineBasicMaterial({
                color: Math.random() > 0.5 ? 0xffffff : 0x60a5fa,
                transparent: true,
                opacity: 0.35
            });
            const line = new THREE.Line(lineGeometry, lineMaterial);

            // Add a "node" (moving point)
            const planeGeo = new THREE.SphereGeometry(0.018, 8, 8);
            const planeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
            const plane = new THREE.Mesh(planeGeo, planeMat);

            return { line, plane, curve, progress: Math.random(), speed: 0.001 + Math.random() * 0.002 };
        };

        const routes: any[] = [];
        for (let i = 0; i < 50; i++) { // Increased to 50
            const route = createRoute();
            routesGroup.add(route.line);
            routesGroup.add(route.plane);
            routes.push(route);
        }

        camera.position.z = size < 400 ? 5.2 : 4.8; /* Adjust zoom based on size */

        // Animation loop
        let animationFrameId: number;
        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);

            routes.forEach(r => {
                r.progress += r.speed; // Use individual speed
                if (r.progress > 1) {
                    r.progress = 0;
                    // Optionally randomize speed on restart for even more variety
                    r.speed = 0.001 + Math.random() * 0.002;
                }
                const pos = r.curve.getPoint(r.progress);
                r.plane.position.copy(pos);
            });

            controls.update();
            renderer.render(scene, camera);
        };

        animate();

        // Cleanup
        return () => {
            cancelAnimationFrame(animationFrameId);
            if (container && renderer.domElement) {
                container.removeChild(renderer.domElement);
            }
            renderer.dispose();
            geometry.dispose();
            material.dispose();
            wireGeometry.dispose();
            wireMaterial.dispose();
        };
    }, [size]);

    return (
        <div
            ref={containerRef}
            style={{
                width: `${size}px`,
                height: `${size}px`,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'grab'
            }}
        />
    );
};

export default Globe3D;
